import type { Locale } from "@mfa/v5-contracts";

/**
 * The Privacy and Terms pages, as plain sections. The text states what the product
 * actually does today (owner decision 2026-10-09: an adult product; Firebase holds the
 * credentials; one session cookie; Stripe handles payment; workspace data is never
 * sold). The owner reviews both texts at the Section 1 acceptance walk; change the copy
 * here and the date below together.
 */
export type LegalDocument = "privacy" | "terms";

export interface LegalSection {
  title: string;
  paragraphs: readonly string[];
}

export interface LegalText {
  title: string;
  updated: string;
  intro: string;
  sections: readonly LegalSection[];
}

export const LEGAL_UPDATED = "2026-10-09";

const en: Record<LegalDocument, LegalText> = {
  privacy: {
    title: "Privacy",
    updated: `Last updated ${LEGAL_UPDATED}`,
    intro:
      "My First Agent and Launch Your Agent are operated by New Machine, Inc. This page says what we collect, why, and what we never do with it.",
    sections: [
      {
        title: "What we collect",
        paragraphs: [
          "Your account: the email address, display name and preferred language that your sign-in provider gives us. Sign-in is handled by Google Firebase Authentication; we never see or store your password.",
          "Your workspace: the agents you create, the files you upload, the instructions you give them, and the transcripts and outputs of their runs.",
          "Payments: when you subscribe, Stripe collects and stores your card details. We store only the customer and subscription identifiers Stripe gives us, and the status of your subscription.",
          "Operations: our servers keep request logs with request ids and timestamps, and the browser reports errors (an error message, the page path and the build id) so we can fix them. Neither contains your prompts or your files.",
        ],
      },
      {
        title: "How we use it",
        paragraphs: [
          "To run the product: to sign you in, keep your workspace, run your agents and bill your subscription.",
          "To run your agents, the instructions, files and messages you give an agent are sent to the AI provider that agent runs on (Anthropic, Microsoft Azure AI Foundry or OpenRouter) for the duration of the run, under that provider's terms.",
          "We never sell your data, and we never use your workspace to advertise to you or to anyone else.",
        ],
      },
      {
        title: "Cookies",
        paragraphs: [
          "One cookie keeps you signed in. It is set only on our own hosts and expires on its own. We set no tracking or advertising cookies.",
        ],
      },
      {
        title: "Keeping and deleting",
        paragraphs: [
          "Your workspace stays as long as your account does. Ask us to delete your account and we remove your account, your workspace and your subscription records; the AI providers keep what their own terms say they keep.",
        ],
      },
      {
        title: "Who may use it",
        paragraphs: [
          "The product is for adults. You must be at least 18 years old to create an account.",
        ],
      },
      {
        title: "Changes and contact",
        paragraphs: [
          "When this page changes, the date at the top changes with it. Questions about your data go to New Machine, Inc. through the address on your invoice or the contact details published on the site.",
        ],
      },
    ],
  },
  terms: {
    title: "Terms of service",
    updated: `Last updated ${LEGAL_UPDATED}`,
    intro:
      "These terms govern your use of My First Agent and Launch Your Agent, operated by New Machine, Inc. By creating an account you agree to them.",
    sections: [
      {
        title: "The service",
        paragraphs: [
          "You describe an agent, we build it from real blocks and run it for you on an AI provider. Templates, the builder, the run console and your file workspace are the product.",
        ],
      },
      {
        title: "Your account",
        paragraphs: [
          "You must be at least 18 years old. One person holds one account per site. Keep your sign-in details to yourself; what happens under your account is your responsibility.",
        ],
      },
      {
        title: "Acceptable use",
        paragraphs: [
          "Use the product lawfully. Do not use it to harm others, to break the terms of the AI providers it runs on, to attack the service, or to reach data that is not yours.",
        ],
      },
      {
        title: "Your content",
        paragraphs: [
          "What you create stays yours. You give us permission to store it and to send it to the AI provider your agent runs on, which is what running an agent means. We claim no other rights to it.",
        ],
      },
      {
        title: "AI output",
        paragraphs: [
          "Agents make mistakes. Check their output before you rely on it. We do not warrant that any output is accurate, complete or fit for a purpose.",
        ],
      },
      {
        title: "Plans and payment",
        paragraphs: [
          "The free plan is free. Paid plans are billed by Stripe at the prices shown on the pricing page, monthly or yearly, and renew until you cancel. Cancel at any time; your plan stays active until the end of the period you paid for. Every run has a spending limit that stops it before it can run away.",
        ],
      },
      {
        title: "Availability and changes",
        paragraphs: [
          "The service is provided as it is. We may change it, add or retire features and providers, and suspend an account that breaks these terms. We will give notice of material changes to these terms on the site.",
        ],
      },
      {
        title: "Liability",
        paragraphs: [
          "To the extent the law allows, New Machine, Inc. is not liable for indirect or consequential losses, and its total liability to you is limited to what you paid us in the twelve months before the claim.",
        ],
      },
    ],
  },
};

const es: Record<LegalDocument, LegalText> = {
  privacy: {
    title: "Privacidad",
    updated: `Última actualización ${LEGAL_UPDATED}`,
    intro:
      "My First Agent y Launch Your Agent son operados por New Machine, Inc. Esta página explica qué recogemos, por qué, y qué nunca hacemos con ello.",
    sections: [
      {
        title: "Qué recogemos",
        paragraphs: [
          "Tu cuenta: la dirección de correo, el nombre visible y el idioma preferido que nos entrega tu proveedor de inicio de sesión. El inicio de sesión lo gestiona Google Firebase Authentication; nunca vemos ni guardamos tu contraseña.",
          "Tu espacio de trabajo: los agentes que creas, los archivos que subes, las instrucciones que les das, y las transcripciones y resultados de sus ejecuciones.",
          "Pagos: cuando te suscribes, Stripe recoge y guarda los datos de tu tarjeta. Nosotros guardamos solo los identificadores de cliente y de suscripción que Stripe nos da, y el estado de tu suscripción.",
          "Operación: nuestros servidores guardan registros de peticiones con identificadores y marcas de tiempo, y el navegador informa de errores (un mensaje, la ruta de la página y el identificador de la versión) para que podamos corregirlos. Ninguno contiene tus instrucciones ni tus archivos.",
        ],
      },
      {
        title: "Cómo lo usamos",
        paragraphs: [
          "Para operar el producto: iniciar tu sesión, conservar tu espacio de trabajo, ejecutar tus agentes y facturar tu suscripción.",
          "Para ejecutar tus agentes, las instrucciones, archivos y mensajes que le das a un agente se envían al proveedor de IA en el que ese agente se ejecuta (Anthropic, Microsoft Azure AI Foundry u OpenRouter) mientras dura la ejecución, bajo los términos de ese proveedor.",
          "Nunca vendemos tus datos, y nunca usamos tu espacio de trabajo para mostrarte publicidad a ti ni a nadie.",
        ],
      },
      {
        title: "Cookies",
        paragraphs: [
          "Una cookie mantiene tu sesión iniciada. Se establece solo en nuestros propios dominios y caduca por sí sola. No usamos cookies de seguimiento ni de publicidad.",
        ],
      },
      {
        title: "Conservación y eliminación",
        paragraphs: [
          "Tu espacio de trabajo se conserva mientras exista tu cuenta. Pídenos eliminar tu cuenta y eliminaremos tu cuenta, tu espacio de trabajo y tus registros de suscripción; los proveedores de IA conservan lo que indiquen sus propios términos.",
        ],
      },
      {
        title: "Quién puede usarlo",
        paragraphs: [
          "El producto es para personas adultas. Debes tener al menos 18 años para crear una cuenta.",
        ],
      },
      {
        title: "Cambios y contacto",
        paragraphs: [
          "Cuando esta página cambie, cambiará también la fecha del encabezado. Las preguntas sobre tus datos se dirigen a New Machine, Inc. a través de la dirección de tu factura o de los datos de contacto publicados en el sitio.",
        ],
      },
    ],
  },
  terms: {
    title: "Términos del servicio",
    updated: `Última actualización ${LEGAL_UPDATED}`,
    intro:
      "Estos términos regulan tu uso de My First Agent y Launch Your Agent, operados por New Machine, Inc. Al crear una cuenta los aceptas.",
    sections: [
      {
        title: "El servicio",
        paragraphs: [
          "Describes un agente, nosotros lo construimos con bloques reales y lo ejecutamos por ti en un proveedor de IA. Las plantillas, el constructor, la consola de ejecución y tu espacio de archivos son el producto.",
        ],
      },
      {
        title: "Tu cuenta",
        paragraphs: [
          "Debes tener al menos 18 años. Una persona tiene una cuenta por sitio. Guarda tus datos de acceso para ti; lo que ocurra bajo tu cuenta es tu responsabilidad.",
        ],
      },
      {
        title: "Uso aceptable",
        paragraphs: [
          "Usa el producto conforme a la ley. No lo uses para dañar a otros, para incumplir los términos de los proveedores de IA en los que se ejecuta, para atacar el servicio, ni para acceder a datos que no son tuyos.",
        ],
      },
      {
        title: "Tu contenido",
        paragraphs: [
          "Lo que creas sigue siendo tuyo. Nos autorizas a guardarlo y a enviarlo al proveedor de IA en el que se ejecuta tu agente, que es lo que significa ejecutar un agente. No reclamamos ningún otro derecho sobre él.",
        ],
      },
      {
        title: "Resultados de la IA",
        paragraphs: [
          "Los agentes cometen errores. Revisa sus resultados antes de confiar en ellos. No garantizamos que ningún resultado sea exacto, completo o adecuado para un fin.",
        ],
      },
      {
        title: "Planes y pago",
        paragraphs: [
          "El plan gratuito es gratis. Los planes de pago los factura Stripe a los precios mostrados en la página de precios, mensual o anualmente, y se renuevan hasta que los canceles. Cancela cuando quieras; tu plan sigue activo hasta el final del periodo pagado. Cada ejecución tiene un límite de gasto que la detiene antes de que se descontrole.",
        ],
      },
      {
        title: "Disponibilidad y cambios",
        paragraphs: [
          "El servicio se ofrece tal cual. Podemos cambiarlo, añadir o retirar funciones y proveedores, y suspender una cuenta que incumpla estos términos. Avisaremos en el sitio de los cambios relevantes en estos términos.",
        ],
      },
      {
        title: "Responsabilidad",
        paragraphs: [
          "En la medida en que la ley lo permita, New Machine, Inc. no responde por pérdidas indirectas o consecuentes, y su responsabilidad total hacia ti se limita a lo que nos pagaste en los doce meses anteriores a la reclamación.",
        ],
      },
    ],
  },
};

export const LEGAL_COPY: Record<Locale, Record<LegalDocument, LegalText>> = { en, es };
